import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { ChevronRight, Camera, Trash2, Plus, Send, ChevronLeft } from "lucide-react";
import { cachedGet, enqueueReport, fileToDataUrl, isOnline, syncQueue } from "../lib/offline";
import { FormFields, fieldKey, uploadDataUrl } from "../components/FormFields";
import { onEvent } from "../lib/realtime";

export default function ReportModal({ projectId, initialDate, forEmployee = false, onClose, onSaved }) {
  const { t, tn } = useI18n();
  const [step, setStep] = useState(1);
  const [workDate, setWorkDate] = useState(initialDate || new Date().toISOString().slice(0, 10));
  const [minutes, setMinutes] = useState("");
  const [assignable, setAssignable] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [reason, setReason] = useState("");
  const [reasonNote, setReasonNote] = useState("");
  const [projects, setProjects] = useState([]);
  const [projId, setProjId] = useState(projectId || "");
  const REASONS = ["PHONE_BROKEN", "NO_PHONE_ACCESS", "MISSED_REPORT", "OFFLINE", "CORRECTION", "OTHER"];
  const [groups, setGroups] = useState([]);
  const [subs, setSubs] = useState([]);
  const [works, setWorks] = useState([]);
  const [units, setUnits] = useState([]);
  const [sel, setSel] = useState({ group: null, sub: null, work: null });
  const [qty, setQty] = useState("");
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]);
  const [cart, setCart] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [custom, setCustom] = useState({});
  const extra = form?.extra_fields || [];
  const totalSteps = extra.length ? 5 : 4;

  useEffect(() => {
    cachedGet("/forms/active").then(setForm).catch(() => setForm(null));
    cachedGet("/config/work-groups").then(setGroups).catch(() => toast.error(t("error")));
    cachedGet("/config/units").then(setUnits).catch(() => {});
    if (forEmployee) cachedGet("/reports-assignable").then((d) => setAssignable(Array.isArray(d) ? d : [])).catch(() => setAssignable([]));
    if (!projectId) cachedGet("/projects").then((d) => { setProjects(d); setProjId((p) => p || d[0]?.id || ""); }).catch(() => {});
  }, [t, forEmployee, projectId]);
  const targetName = assignable.find((e) => e.id === employeeId)?.name;

  useEffect(() => onEvent(async (ev) => {
    if (ev.type !== "config.changed") return;
    const e = ev.payload?.entity || "";
    if (e === "work-groups") setGroups(await cachedGet("/config/work-groups"));
    if (e === "units") setUnits(await cachedGet("/config/units"));
    try {
      if (e === "work-subgroups" && sel.group) setSubs(await cachedGet(`/config/work-subgroups?group_id=${sel.group.id}`));
      if (e === "work-types" && sel.sub) setWorks(await cachedGet(`/config/work-types?subgroup_id=${sel.sub.id}`));
    } catch {}
  }), [sel.group, sel.sub]);

  const pickGroup = async (g) => {
    setSel({ group: g, sub: null, work: null });
    try { setSubs(await cachedGet(`/config/work-subgroups?group_id=${g.id}`)); setStep(2); }
    catch { toast.error(isOnline() ? t("error") : t("offline_no_cache")); }
  };
  const pickSub = async (s) => {
    setSel((p) => ({ ...p, sub: s, work: null }));
    try { setWorks(await cachedGet(`/config/work-types?subgroup_id=${s.id}`)); setStep(3); }
    catch { toast.error(isOnline() ? t("error") : t("offline_no_cache")); }
  };
  const pickWork = (w) => { setSel((p) => ({ ...p, work: w })); setStep(4); };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const local = { blob: file, data: await fileToDataUrl(file), name: file.name };
    if (!isOnline()) { setPhotos((p) => [...p, { pending: local }]); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("entity_type", "report"); fd.append("phase", "during");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, data]);
    } catch { setPhotos((p) => [...p, { pending: local }]); }
  };

  const timeBased = sel.work?.measurement_type === "TIME_BASED";
  const addToCart = () => {
    if (!timeBased && !qty) return toast.error(t("quantity"));
    if (!minutes || Number(minutes) <= 0) return toast.error(t("actual_required"));
    const unit = units.find((u) => u.id === sel.work.unit_id);
    setCart((c) => [...c, {
      work_group_id: sel.group.id, work_subgroup_id: sel.sub.id, work_type_id: sel.work.id,
      unit_id: sel.work.unit_id, quantity: timeBased ? 1 : parseFloat(qty), actual_minutes: parseInt(minutes, 10), comment,
      photo_ids: photos.filter((p) => p.id).map((p) => p.id),
      pending_photos: photos.filter((p) => p.pending).map((p) => ({ blob: p.pending.blob, name: p.pending.name })),
      _name: tn(sel.work.name), _unit: timeBased ? "" : (unit ? tn(unit.name) : ""),
    }]);
    setSel({ group: null, sub: null, work: null }); setQty(""); setMinutes(""); setComment(""); setPhotos([]); setStep(1);
    toast.success(t("added"));
  };

  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("entity_type", "report"); fd.append("phase", "during");
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); return { id: data.id, name: file.name }; }
    catch { toast.error(t("upload_failed")); return null; }
  };
  const missingRequired = () => extra.some((f, i) => { const v = custom[fieldKey(f, i)]; return f.required && (v === undefined || v === "" || v === null || v === false); });

  const submit = async () => {
    if (cart.length === 0) return toast.error(t("no_data"));
    if (extra.length && step !== 5) { setStep(5); return; }
    if (missingRequired()) return toast.error(t("fill_required"));
    if (forEmployee && !employeeId) return toast.error(t("for_employee"));
    if (forEmployee && !reason) return toast.error(t("reason_required"));
    setBusy(true);
    const date = workDate;
    const meta = { project_id: projId, date, work_date: date, employee_id: forEmployee ? employeeId : undefined,
      created_for_reason: forEmployee ? (reasonNote.trim() ? `${reason}: ${reasonNote.trim()}` : reason) : undefined };
    const hasPending = cart.some((c) => c.pending_photos?.length);
    try {
      if (!isOnline() || hasPending) throw new Error("offline");
      const items = cart.map(({ pending_photos, _name, _unit, ...rest }) => rest);
      const payload = { ...meta, items, custom_fields: {}, gps: null, signature_file_id: null };
      for (let i = 0; i < extra.length; i++) {
        const f = extra[i], k = fieldKey(f, i), v = custom[k];
        if (v === undefined || v === "") continue;
        if (f.type === "signature") { payload.signature_file_id = await uploadDataUrl(v); payload.custom_fields[k] = payload.signature_file_id; }
        else if (f.type === "gps") { payload.gps = v; payload.custom_fields[k] = `${v.lat},${v.lng}`; }
        else if (f.type === "photo" || f.type === "file") payload.custom_fields[k] = v.id;
        else payload.custom_fields[k] = v;
      }
      const { data } = await api.post("/reports", payload);
      await api.post(`/reports/${data.id}/submit`);
      toast.success(t("submit") + " ✓");
      onSaved();
    } catch (e) {
      if (e.response && e.response.status !== 0) { toast.error(e.response?.data?.detail || t("error")); }
      else {
        await enqueueReport({ ...meta, items: cart, custom_fields: custom, extra_fields: extra });
        toast.success(t("saved_offline"));
        syncQueue();
        onSaved();
      }
    } finally { setBusy(false); }
  };

  const Tile = ({ label, onClick, testid }) => (
    <button data-testid={testid} onClick={onClick}
      className="w-full flex items-center justify-between gap-2 big-touch px-4 py-3 tactical-card hover:border-[var(--amber)] text-left font-semibold text-base transition-colors">
      <span className="min-w-0 break-words">{label}</span> <ChevronRight size={18} className="text-[var(--amber)] shrink-0" />
    </button>
  );
  const crumbs = [sel.group && tn(sel.group.name), sel.sub && tn(sel.sub.name)].filter(Boolean).join(" → ");
  const back = () => setStep((s) => Math.max(1, s - 1));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md w-[calc(100vw-1rem)] sm:w-full p-0 sm:p-0 gap-0 max-h-[92vh] overflow-y-auto rounded-2xl">
        <DialogHeader className="p-4 border-b sticky top-0 bg-[var(--card)] z-10">
          <DialogTitle className="font-display flex items-center gap-2 pr-6 text-base sm:text-lg">
            {step > 1 && <button onClick={back} data-testid="modal-back" className="p-1 -m-1"><ChevronLeft size={20} /></button>}
            {forEmployee ? t("create_report_for") : t("add_work")} · {step}/{totalSteps}
          </DialogTitle>
          {forEmployee && targetName && step > 1 && <div className="text-xs font-bold text-[var(--amber)] mt-1" data-testid="report-for-crumb">{t("report_for")}: {targetName}</div>}
          {crumbs && <div className="text-xs text-[var(--text-3)] mt-1 break-words">{crumbs}</div>}
        </DialogHeader>
        <div className="p-4 space-y-2">
          {step === 1 && (
            <div className="tactical-card p-3 space-y-2" data-testid="report-meta">
              {forEmployee && (
                <div className="space-y-2">
                  <div>
                    <label className="text-[11px] uppercase font-semibold text-[var(--text-3)]">{t("for_employee")} *</label>
                    <select data-testid="report-employee-select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="w-full h-10 border border-[var(--border)] rounded-md px-2 text-sm bg-[var(--bg)]">
                      <option value="">—</option>
                      {assignable.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </div>
                  {targetName && <div className="rounded-lg px-3 py-2 bg-[var(--amber)]/15 border border-[var(--amber)] text-sm font-bold" data-testid="report-for-banner">{t("report_for")}: {targetName}</div>}
                  <div className="grid grid-cols-2 gap-2">
                    <select data-testid="report-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full h-10 border border-[var(--border)] rounded-md px-2 text-sm bg-[var(--bg)]">
                      <option value="">{t("reason")} *</option>
                      {REASONS.map((r) => <option key={r} value={r}>{t(`reason_${r}`)}</option>)}
                    </select>
                    <Input data-testid="report-reason-note" placeholder={t("comment")} value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} className="h-10" />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 items-end">
                <div>
                  <label className="text-[11px] uppercase font-semibold text-[var(--text-3)]">{t("work_date")}</label>
                  <Input data-testid="report-work-date" type="date" value={workDate} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setWorkDate(e.target.value)} className="h-10" />
                </div>
                {!projectId && (
                  <div>
                    <label className="text-[11px] uppercase font-semibold text-[var(--text-3)]">{t("project")}</label>
                    <select data-testid="report-project-select" value={projId} onChange={(e) => setProjId(e.target.value)} className="w-full h-10 border border-[var(--border)] rounded-md px-2 text-sm bg-[var(--bg)]">
                      {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}
          {step === 1 && <div className="text-xs uppercase font-semibold text-[var(--text-3)]">{t("work_group")}</div>}
          {step === 1 && groups.map((g) => <Tile key={g.id} testid={`grp-${g.id}`} label={tn(g.name)} onClick={() => pickGroup(g)} />)}
          {step === 2 && <div className="text-xs uppercase font-semibold text-[var(--text-3)]">{t("subgroup")}</div>}
          {step === 2 && subs.map((s) => <Tile key={s.id} testid={`sub-${s.id}`} label={tn(s.name)} onClick={() => pickSub(s)} />)}
          {step === 3 && <div className="text-xs uppercase font-semibold text-[var(--text-3)]">{t("work")}</div>}
          {step === 3 && works.map((w) => <Tile key={w.id} testid={`work-${w.id}`} label={tn(w.name)} onClick={() => pickWork(w)} />)}
          {step === 4 && (
            <div className="space-y-3">
              <div className="font-display text-lg font-bold break-words">{tn(sel.work?.name)}
                {timeBased && <span className="ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded bg-[var(--bg)] text-[var(--text-3)] align-middle" data-testid="time-based-badge">{t("TIME_BASED")}</span>}
                {sel.work?.activity_type === "NON_PRODUCTIVE" && <span className="ml-1 text-[10px] uppercase px-1.5 py-0.5 rounded bg-[var(--bg)] text-[var(--amber)] align-middle">{t("NON_PRODUCTIVE")}</span>}
              </div>
              {!timeBased && <div>
                <label className="text-xs uppercase font-semibold text-[var(--text-3)]">{t("quantity")} · {tn(units.find((u) => u.id === sel.work?.unit_id)?.name)}</label>
                <Input data-testid="qty-input" type="number" inputMode="decimal" value={qty}
                  onChange={(e) => setQty(e.target.value)} className="h-14 text-2xl font-mono-num text-center" autoFocus />
              </div>}
              <div>
                <label className="text-xs uppercase font-semibold text-[var(--text-3)]">{t("actual_minutes")} *</label>
                <Input data-testid="minutes-input" type="number" inputMode="numeric" min="1" value={minutes}
                  onChange={(e) => setMinutes(e.target.value)} className="h-14 text-2xl font-mono-num text-center" />
              </div>
              <Textarea data-testid="item-comment" placeholder={t("comment")} value={comment} onChange={(e) => setComment(e.target.value)} className="text-base" />
              <div className="flex items-center gap-2 flex-wrap">
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 h-12 tactical-card text-sm font-semibold" data-testid="photo-upload-label">
                  <Camera size={18} /> {t("photos")} {photos.length > 0 && `(${photos.length})`}
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadPhoto} data-testid="photo-input" />
                </label>
                {photos.map((p, i) => <img key={i} alt="" src={p.pending ? p.pending.data : undefined} className={`w-12 h-12 rounded object-cover ${p.pending ? "" : "hidden"}`} />)}
                {photos.filter((p) => p.id).map((p) => <span key={p.id} className="text-xs bg-slate-100 px-2 py-1 rounded">{p.original_filename?.slice(0, 12)}</span>)}
              </div>
              <Button data-testid="add-item-btn" onClick={addToCart} className="w-full h-12 bg-[var(--amber)] hover:brightness-95 text-white font-bold gap-2">
                <Plus size={18} /> {t("save")}
              </Button>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-3" data-testid="form-step">
              <div className="font-display text-lg font-bold">{t("form_step")}</div>
              <FormFields fields={extra} values={custom} onChange={setCustom} uploadFile={uploadFile} />
            </div>
          )}

          {cart.length > 0 && (
            <div className="tactical-card p-3 mt-3" data-testid="cart">
              <div className="text-xs uppercase font-semibold text-[var(--text-3)] mb-2">{t("today_done")} ({cart.length})</div>
              {cart.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-sm py-1.5 min-w-0">
                  <span className="flex-1 min-w-0 break-words">{c._name}</span>
                  <span className="font-mono-num font-semibold shrink-0 whitespace-nowrap">{c.quantity} {c._unit}</span>
                  <span className="font-mono-num text-xs text-[var(--text-3)] shrink-0">{c.actual_minutes}′</span>
                  <button onClick={() => setCart((x) => x.filter((_, j) => j !== i))} data-testid={`del-cart-${i}`} className="p-2 -m-2">
                    <Trash2 size={16} className="text-[var(--red)]" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="p-4 border-t sticky bottom-0 bg-[var(--card)] flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 min-w-0 h-12">{t("cancel")}</Button>
          <Button data-testid="submit-report-btn" onClick={submit} disabled={busy || cart.length === 0}
            className="flex-1 min-w-0 h-12 bg-[var(--green)] hover:brightness-95 text-white font-bold gap-2">
            <Send size={16} /> {extra.length && step !== 5 ? t("next") : t("submit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
